import React, { useState } from 'react';
import { Reference } from '../../../types/profile';
import { EditableSection } from '../common/EditableSection';
import { FormInput } from '../common/FormInput';

interface ReferencesSectionProps {
  references: Reference[];
  onUpdate: (references: Reference[]) => void;
}

interface ReferenceFormProps {
  reference: Reference;
  onChange: (reference: Reference) => void;
  onRemove: () => void;
}

const COUNTRY_CODES = [
  { code: '+1', country: 'US/CA' },
  { code: '+44', country: 'UK' },
  { code: '+91', country: 'IN' },
  { code: '+86', country: 'CN' },
  { code: '+81', country: 'JP' },
  { code: '+49', country: 'DE' },
  { code: '+33', country: 'FR' },
  { code: '+39', country: 'IT' },
  { code: '+34', country: 'ES' },
  { code: '+61', country: 'AU' },
  { code: '+55', country: 'BR' },
  { code: '+52', country: 'MX' },
  { code: '+82', country: 'KR' },
  { code: '+31', country: 'NL' },
  { code: '+46', country: 'SE' },
  { code: '+47', country: 'NO' },
  { code: '+48', country: 'PL' },
  { code: '+41', country: 'CH' },
  { code: '+43', country: 'AT' },
  { code: '+32', country: 'BE' },
  { code: '+45', country: 'DK' },
  { code: '+358', country: 'FI' },
  { code: '+353', country: 'IE' },
  { code: '+351', country: 'PT' },
  { code: '+30', country: 'GR' },
  { code: '+420', country: 'CZ' },
  { code: '+36', country: 'HU' },
  { code: '+40', country: 'RO' },
  { code: '+7', country: 'RU' },
  { code: '+380', country: 'UA' },
  { code: '+27', country: 'ZA' },
  { code: '+234', country: 'NG' },
  { code: '+20', country: 'EG' },
  { code: '+212', country: 'MA' },
  { code: '+216', country: 'TN' },
  { code: '+254', country: 'KE' },
  { code: '+255', country: 'TZ' },
  { code: '+256', country: 'UG' },
  { code: '+233', country: 'GH' },
  { code: '+237', country: 'CM' },
  { code: '+225', country: 'CI' },
  { code: '+221', country: 'SN' },
  { code: '+60', country: 'MY' },
  { code: '+62', country: 'ID' },
  { code: '+63', country: 'PH' },
  { code: '+65', country: 'SG' },
  { code: '+66', country: 'TH' },
  { code: '+84', country: 'VN' },
  { code: '+880', country: 'BD' },
  { code: '+92', country: 'PK' },
  { code: '+93', country: 'AF' },
  { code: '+94', country: 'LK' },
  { code: '+95', country: 'MM' },
  { code: '+977', country: 'NP' },
  { code: '+90', country: 'TR' },
  { code: '+98', country: 'IR' },
  { code: '+964', country: 'IQ' },
  { code: '+966', country: 'SA' },
  { code: '+971', country: 'AE' },
  { code: '+972', country: 'IL' },
  { code: '+962', country: 'JO' },
  { code: '+961', country: 'LB' },
  { code: '+963', country: 'SY' },
  { code: '+970', country: 'PS' },
  { code: '+968', country: 'OM' },
  { code: '+967', country: 'YE' },
  { code: '+965', country: 'KW' },
  { code: '+973', country: 'BH' },
  { code: '+974', country: 'QA' },
].sort((a, b) => a.code.localeCompare(b.code));

const ReferenceForm: React.FC<ReferenceFormProps> = ({ reference, onChange, onRemove }) => {
  const [countryCode, setCountryCode] = useState('+1');
  const [phoneNumber, setPhoneNumber] = useState('');

  React.useEffect(() => {
    if (reference.mobile) {
      const parts = reference.mobile.split(' ');
      if (parts.length >= 2) {
        setCountryCode(parts[0]);
        setPhoneNumber(parts.slice(1).join(' '));
      } else {
        setPhoneNumber(reference.mobile);
      }
    }
  }, [reference.mobile]);

  const handleFieldChange = (field: keyof Reference) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    onChange({ ...reference, [field]: e.target.value });
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9\s-]/g, '');
    setPhoneNumber(value);
    onChange({ ...reference, mobile: `${countryCode} ${value}`.trim() });
  };

  const handleCountryCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newCode = e.target.value;
    setCountryCode(newCode);
    onChange({ ...reference, mobile: `${newCode} ${phoneNumber}`.trim() });
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 space-y-4">
      <div className="flex justify-between items-start">
        <h4 className="text-lg font-medium text-gray-900">Reference Entry</h4>
        <button
          type="button"
          onClick={onRemove}
          className="text-red-600 hover:text-red-800"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormInput
          label="Name"
          name="name"
          value={reference.name}
          onChange={handleFieldChange('name')}
          maxLength={100}
        />
        <FormInput
          label="Title"
          name="title"
          value={reference.title}
          onChange={handleFieldChange('title')}
          maxLength={100}
        />
        <FormInput
          label="Company"
          name="company"
          value={reference.company}
          onChange={handleFieldChange('company')}
          maxLength={100}
        />
        <FormInput
          label="Email"
          name="email"
          type="email"
          value={reference.email}
          onChange={handleFieldChange('email')}
          maxLength={100}
        />
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Phone Number
          </label>
          <div className="flex space-x-2">
            <select
              value={countryCode}
              onChange={handleCountryCodeChange}
              className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              {COUNTRY_CODES.map(({ code, country }) => (
                <option key={code} value={code}>
                  {code} ({country})
                </option>
              ))}
            </select>
            <input
              type="tel"
              value={phoneNumber}
              onChange={handlePhoneChange}
              placeholder="Phone number"
              className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export const ReferencesSection: React.FC<ReferencesSectionProps> = ({
  references,
  onUpdate
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editReferences, setEditReferences] = useState<Reference[]>(references);

  const handleEdit = () => {
    setEditReferences(references);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditReferences(references);
    setIsEditing(false);
  };

  const handleSave = () => {
    onUpdate(editReferences);
    setIsEditing(false);
  };

  const handleAddReference = () => {
    setEditReferences([
      ...editReferences,
      {
        name: '',
        title: '',
        company: '',
        mobile: '',
        email: ''
      }
    ]);
  };

  const handleUpdateReference = (index: number) => (ref: Reference) => {
    const updated = [...editReferences];
    updated[index] = ref;
    setEditReferences(updated);
  };

  const handleRemoveReference = (index: number) => () => {
    setEditReferences(editReferences.filter((_, i) => i !== index));
  };

  return (
    <EditableSection
      title="References"
      isEditing={isEditing}
      onEdit={handleEdit}
      onSave={handleSave}
      onCancel={handleCancel}
    >
      {isEditing ? (
        <div className="space-y-4">
          {editReferences.map((ref, index) => (
            <ReferenceForm
              key={index}
              reference={ref}
              onChange={handleUpdateReference(index)}
              onRemove={handleRemoveReference(index)}
            />
          ))}
          <button
            type="button"
            onClick={handleAddReference}
            className="w-full py-2 px-4 border-2 border-dashed border-gray-300 rounded-md text-sm font-medium text-gray-600 hover:border-gray-400 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            + Add Reference
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {references.length > 0 ? (
            references.map((ref, index) => (
              <div key={index} className="border-b border-gray-200 pb-4 last:border-0">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">{ref.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {ref.title} {ref.company && `at ${ref.company}`}
                    </p>
                    <div className="mt-2 space-y-1">
                      {ref.mobile && (
                        <p className="text-sm text-gray-600">📞 {ref.mobile}</p>
                      )}
                      {ref.email && (
                        <p className="text-sm text-gray-600">✉️ {ref.email}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p className="text-gray-500 italic">No references added yet.</p>
          )}
        </div>
      )}
    </EditableSection>
  );
};